import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useParams} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {usePaymentMethods} from '../hooks/usePaymentMethods';
import {usePaymentMethodsFilter} from '../hooks/usePaymentMethodsFilter';
import {usePaymentMethodsSearch} from '../hooks/usePaymentMethodsSearch';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {PaymentMethod} from '../types';
import {UsePaymentMethodsSelectionResult} from '../hooks/usePaymentMethodsSelection';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + RowSelectionDataCell + the app-local PaginationControls) to the
// shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all three come from the shared
// component. Five checklist gaps close by changing this one component (§3z).
//
// ⚠️ NOTE FOR THE REMAINING MODULES: `src/components/PaginationControls` and
// `src/hooks/usePagination` are NOT deleted here. Other modules still import
// them; they come out in a later wave once every module is migrated.
//
// ⚠️ MONEY-PATH ADJACENT. This module also renders Stripe Elements and is
// consumed by payment-intents. Only the LIST/TABLE layer is changed: no payment
// logic, no Element wiring, no confirm/capture flow.
//
// The old table opened an inline EDIT DRAWER on row click, submitting through
// `update()` — a mutation on a saved payment method. That is payment surface,
// so it is NOT rebuilt here: rows are inert rather than being re-plumbed into a
// flow this pass is not allowed to touch. The drawer components are untouched
// and still exported for the module's own pages.

interface RecordsTableProps {
    /**
     * Selection is read by IndexPage and passed down, because the delete
     * button lives in the page HEADER — a sibling, not a child of this table.
     * The state itself is the Redux payment-methods.selection slice (FNP-951);
     * taking it as a prop keeps this component store-free and testable.
     */
    selection?: UsePaymentMethodsSelectionResult;
    /**
     * The customer whose payment methods to list.
     *
     * ⚠️ This is a PROP, not a route param — customers/pages/EditPage renders
     * `<PaymentMethodsTable customerId={customerId}/>` inside a tab. The route
     * param is read only as a fallback for a standalone mount.
     */
    customerId?: string;
}

export const RecordsTable = ({
                                 selection,
                                 customerId: customerIdProp,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions();
    const {id: customerIdParam} = useParams();

    // The prop wins; the route param is the fallback for a standalone mount.
    // An empty id disables the query rather than firing a request the API would
    // reject (customerId is required by PaymentMethodsFilterInput).
    const customerId = customerIdProp ?? customerIdParam ?? '';

    const {filters} = usePaymentMethodsFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = usePaymentMethods(customerId, filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = usePaymentMethodsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The select column is HIDDEN, not disabled, when the action is not
    // permitted (§2, §10). Gated on the same action the delete button is gated
    // on — a checkbox with no permitted batch action behind it is an affordance
    // that does nothing.
    const canSelect = !!selection
        && allowedActions.includes(rbacActions.DeletePaymentMethod);

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection.selection[item.id])
        : undefined;

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no payment methods" sends an
    // operator hunting for a bug when a search simply matched nothing.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No payment methods match this search')
        : I18n.get('No payment methods found');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load payment methods')}
                >
                    {I18n.get(
                        'The billing API did not return the payment method list. Try Reload; '
                        + 'if it persists the API may be unavailable in this environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<PaymentMethod>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records: PaymentMethod[]) =>
                        selection.handleSelectAllRows(true, records)
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'payment method'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
